import { IAuthorDetail, IBookDetail } from "~/utils/interface";
import { IcoChilli, IcoPlayer } from "~/icons";
import React, { useEffect, useRef, useState } from "react";
import {
  capitalizeFirstLetter,
  getFirstLetter,
  secondsToDaysHoursMinutes,
  sendToDataLayer,
} from "~/utils/helpers";
import { AudioPlayer, Description } from "~/components";
import { useWindowWidth } from "~/utils/hooks/useWindowWidth";
import Equalizer from "~/components/Equalizer";

export const BookHome = ({
  id,
  authors,
  title,
  description,
  tags,
  authors_data,
  cover,
  spicy,
  narrator_type,
  narrators,
  mood,
  rating,
  sample,
  serial_number,
  series_name,
  series,
  emoji,
  total_duration,
  narrators_data,
  free_chapters,
  is_free,
  total_chapters,
}: IBookDetail) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const width = useWindowWidth();

  const authors_narrators: (IAuthorDetail & { type: "Author" | "Narrator" })[] =
    [
      ...authors_data.map((author) => ({
        ...author,
        type: "Author",
      })),
      ...narrators_data.map((narrator) => ({
        ...narrator,
        type: "Narrator",
      })),
    ];

  const handleSampleClick = () => {
    if (!sample) return;

    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current = new Audio(
        `https://static.wromance.com/${id}/${sample}`,
      );
      if ("play" in audioRef.current) {
        audioRef.current.play();
        sendToDataLayer({ event: "sample_played", property: id });
      }
      setIsPlaying(true);

      if ("onended" in audioRef.current) {
        audioRef.current.onended = () => {
          setIsPlaying(false);
        };
      }
    }
  };

  const handleClickListen = () => {
    if (isPlaying) {
      handleSampleClick();
    }
    if (!is_free) {
      sendToDataLayer({ event: "open_checkout" });
      window.open(`${import.meta.env.VITE_PAYWALL_URL}`, "_blank");
    } else {
      sendToDataLayer({ event: "audio_played", property: id });
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isModalOpen]);

  return (
    <>
      <section className="max-md:pt-[108px] pt-[200px]">
        <div className="container">
          <div className="grid max-md:gap-0 max-md:grid-cols-1 max-lg:gap-8 grid-cols-[324px_1fr] gap-[60px]">
            <div>
              <div className="relative max-md:w-[324px] max-md:max-w-full max-md:mx-auto">
                <img
                  className="rounded-2xl shadow-2xl"
                  width={324}
                  height={324}
                  src={`https://static.wromance.com/cdn-cgi/image/width=324,quality=90/${id}/${cover}`}
                  alt={title}
                />
                <div className="absolute shadow-[0px_4px_99px_5px_rgba(31,61,105,0.9)] w-full h-full top-0 left-0 z-10" />
                <div className="absolute shadow-[0px_4px_100px_12px_rgba(31,61,105,0.9)] w-full h-full top-0 left-0 z-10" />
                <div className="absolute shadow-[0px_4px_13.1px_17px_rgba(0,0,0,0.34)] w-full h-full top-0 left-0 z-10" />
              </div>
              {width >= 768 && (
                <div className="mt-6 relative z-10">
                  <button
                    onClick={handleClickListen}
                    className="max-lg:leading-[22px] max-lg:text-[16px] flex w-full justify-center text-white p-4 items-center font-bold leading-6 text-xl bg-[#335495] rounded-xl"
                  >
                    Listen <IcoPlayer className="ml-2" width={20} height={20} />
                  </button>
                  <button
                    onClick={handleSampleClick}
                    className="max-lg:leading-[22px] max-lg:text-[16px] flex w-full justify-center text-[#78A7ED] p-4 items-center font-bold leading-6 text-xl border border-[#78A7ED] rounded-xl mt-6"
                  >
                    Sample
                    <Equalizer isPlaying={isPlaying} />
                  </button>
                </div>
              )}
            </div>
            <div>
              {width < 768 && tags && (
                <ul className="mt-4 flex flex-wrap max-md:justify-center relative z-10">
                  {tags.map((tag, index) => (
                    <li
                      className="opacity-85 max-lg:leading-[22px] max-lg:text-[16px] leading-7 text-xl last:mr-0 text-white mr-2"
                      key={`tag_${index}`}
                    >
                      {index !== 0 && "•"} {tag}
                    </li>
                  ))}
                </ul>
              )}
              <h1 className="max-md:hidden max-lg:text-[40px] max-lg:leading-[55px] leading-[80px] text-[48px] text-white font-extrabold">
                {title}
              </h1>
              {series_name && (
                <h3 className="max-md:mt-3 max-md:font-normal max-md:text-center max-lg:leading-[22px] max-lg:text-[16px] text-[#87B7FF] font-semibold leading-[49px] text-4xl">
                  Series: {series_name} #{serial_number}
                </h3>
              )}
              {width < 768 && (
                <div className="mt-6 relative z-10">
                  <button
                    onClick={handleClickListen}
                    className="max-lg:leading-[22px] max-lg:text-[16px] flex w-full justify-center text-white p-4 items-center font-bold leading-6 text-xl bg-[#335495] rounded-xl"
                  >
                    Listen <IcoPlayer className="ml-2" width={20} height={20} />
                  </button>
                  <button
                    onClick={handleSampleClick}
                    className="max-lg:mt-4 max-lg:leading-[22px] max-lg:text-[16px] flex w-full justify-center text-[#78A7ED] p-4 items-center font-bold leading-6 text-xl border border-[#78A7ED] rounded-xl mt-6"
                  >
                    Sample
                    <Equalizer isPlaying={isPlaying} />
                  </button>
                </div>
              )}
              {width >= 768 && tags && (
                <ul className="mt-4 flex flex-wrap">
                  {tags.map((tag, index) => (
                    <li
                      className="opacity-85 max-lg:leading-[22px] max-lg:text-[16px] leading-7 text-xl last:mr-0 text-white mr-2"
                      key={`tag_${index}`}
                    >
                      {index !== 0 && "•"} {tag}
                    </li>
                  ))}
                </ul>
              )}

              <div className="max-lg:mt-6 mt-8 flex max-md:justify-center">
                <div className="text-white pr-8">
                  <h4 className="max-lg:h-[22px] max-lg:leading-[22px] max-lg:text-[16px] text-xl leading-7 h-7">
                    {secondsToDaysHoursMinutes(total_duration)}
                  </h4>
                  <div className="max-lg:text-[13px] max-lg:leading-[18px] opacity-70 text-center mt-1 text-[16px] leading-[22px]">
                    Duration
                  </div>
                </div>
                <div className="text-white px-8 relative after:right-0 after:bg-[rgba(255,_255,_255,_0.3)] after:block after:w-[1px] after:h-9 after:absolute after:top-0 before:left-0 before:bg-[rgba(255,_255,_255,_0.3)] before:block before:w-[1px] before:h-9 before:absolute before:top-0">
                  <h4 className="max-lg:h-[22px] max-lg:leading-[22px] max-lg:text-[16px] text-xl text-center leading-7 h-7">
                    {capitalizeFirstLetter(narrator_type)}
                  </h4>
                  <div className="max-lg:text-[13px] max-lg:leading-[18px] opacity-70 text-center mt-1 text-[16px] leading-[22px]">
                    Narration
                  </div>
                </div>
                <div className="text-white pl-8">
                  <h4 className="max-lg:h-5 max-lg:leading-[22px] max-lg:text-[16px] text-xl leading-7 h-[27px]">
                    <ul className="flex">
                      {Array.from({ length: spicy }, (_, index) => (
                        <li key={`pepper_${index}`}>
                          <img
                            src={IcoChilli}
                            alt={`pepper_${index}`}
                            key={`pepper_${index}`}
                            className="mr-1 h-5 w-5"
                          />
                        </li>
                      ))}
                    </ul>
                  </h4>
                  <div className="max-lg:text-[13px] max-lg:leading-[18px] opacity-70 text-center mt-1 text-[16px] leading-[22px]">
                    Spice
                  </div>
                </div>
              </div>
              <Description description={description} />
              {authors_narrators && (
                <ul className="mt-8 flex max-lg:flex-wrap max-md:flex-wrap max-md:justify-center">
                  {authors_narrators.map((author, index) => (
                    <li className="w-[100px]" key={author.id}>
                      <div className="w-[82px] h-[82px] rounded-full mx-auto overflow-hidden bg-[#335495] flex items-center justify-center">
                        {author.photo ? (
                          <img
                            width={82}
                            height={82}
                            className="rounded-full"
                            src={`https://static.wromance.com/${author.photo}`}
                            alt={author.name}
                          />
                        ) : (
                          <div className="font-bold text-[22px] leading-[30px] text-white">
                            {getFirstLetter(author.name)}
                            {getFirstLetter(author.surname)}
                          </div>
                        )}
                      </div>
                      <div className="max-lg:leading-[22px] max-lg:text-[16px] mt-[6px] text-center text-lg leading-6 text-white">
                        {author.name} <br /> {author.surname}
                      </div>
                      <div className="max-lg:leading-[18px] max-lg:text-[13px] mt-1 opacity-70 text-[16px] leading-[22px] text-white text-center">
                        {author.type}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </section>
      <AudioPlayer
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        cover={cover}
        title={title}
        id={id}
        totalChapters={total_chapters}
        freeChapters={free_chapters}
      />
    </>
  );
};

BookHome.displayName = "BookHome";
export default BookHome;
